import type {
  EngineWarning,
  RenderContext,
  TextRenderDiagnostic,
} from "../types/render";
import type {
  BarcodeClampReason,
  BarcodeDiagnostic,
  LayoutResult,
  PositionedElement,
  ZplEncoding,
} from "../types/template";
import {
  canUseCode128SubsetC,
  estimateCode128Modules,
  estimateQrVersion,
  qrModulesPerSide,
} from "../utils/barcode";
import { mmToDots, mmToSignedDots } from "../utils/units";
import { emitWithLineSafeEngine } from "./textEngines/lineSafeEngine";
import { selectTextEngine } from "./textEngines/selectTextEngine";
import { emitWithTbEngine } from "./textEngines/tbEngine";
import type {
  PreparedTextElement,
  TextEngineContext,
} from "./textEngines/types";

const CP1252_EXTENDED_MAP = new Map<number, number>([
  [0x20ac, 0x80],
  [0x201a, 0x82],
  [0x0192, 0x83],
  [0x201e, 0x84],
  [0x2026, 0x85],
  [0x2020, 0x86],
  [0x2021, 0x87],
  [0x02c6, 0x88],
  [0x2030, 0x89],
  [0x0160, 0x8a],
  [0x2039, 0x8b],
  [0x0152, 0x8c],
  [0x017d, 0x8e],
  [0x2018, 0x91],
  [0x2019, 0x92],
  [0x201c, 0x93],
  [0x201d, 0x94],
  [0x2022, 0x95],
  [0x2013, 0x96],
  [0x2014, 0x97],
  [0x02dc, 0x98],
  [0x2122, 0x99],
  [0x0161, 0x9a],
  [0x203a, 0x9b],
  [0x0153, 0x9c],
  [0x017e, 0x9e],
  [0x0178, 0x9f],
]);

export interface ZplEmitResult {
  zpl: string;
  warnings: EngineWarning[];
  barcodeDiagnostics: BarcodeDiagnostic[];
  textDiagnostics: TextRenderDiagnostic[];
}

function encodeCp1252(value: string): Uint8Array {
  const bytes: number[] = [];

  for (const char of value) {
    const codePoint = char.codePointAt(0);
    if (codePoint === undefined) {
      continue;
    }

    if (codePoint <= 0xff) {
      bytes.push(codePoint);
      continue;
    }

    const extended = CP1252_EXTENDED_MAP.get(codePoint);
    bytes.push(extended ?? 0x3f);
  }

  return new Uint8Array(bytes);
}

function encodeBytes(value: string, encoding: ZplEncoding): Uint8Array {
  if (encoding === "utf-8") {
    return new TextEncoder().encode(value);
  }

  return encodeCp1252(value);
}

function isPlainSafe(value: string): boolean {
  return /^[\x20-\x7E]*$/.test(value) && !/[\^~]/.test(value);
}

function toHex(byte: number): string {
  return byte.toString(16).toUpperCase().padStart(2, "0");
}

function encodeFieldData(
  value: string,
  encoding: ZplEncoding,
): {
  text: string;
  useHex: boolean;
} {
  if (isPlainSafe(value)) {
    return {
      text: value,
      useHex: false,
    };
  }

  const bytes = encodeBytes(value, encoding);
  const escaped = Array.from(bytes)
    .map((byte) => `_${toHex(byte)}`)
    .join("");

  return {
    text: escaped,
    useHex: true,
  };
}

function boolFlag(value: boolean): "Y" | "N" {
  return value ? "Y" : "N";
}

function offsetX(
  positioned: PositionedElement,
  ctx: RenderContext,
  offsetDotsX: number,
): number {
  return Math.max(0, mmToDots(positioned.xMm, ctx.dpi) + offsetDotsX);
}

function offsetY(
  positioned: PositionedElement,
  ctx: RenderContext,
  offsetDotsY: number,
): number {
  return Math.max(0, mmToDots(positioned.yMm, ctx.dpi) + offsetDotsY);
}

function prepareTextElement(
  positioned: PositionedElement,
  ctx: RenderContext,
  offsetDotsX: number,
  offsetDotsY: number,
): PreparedTextElement | undefined {
  if (positioned.textValue === undefined) {
    return undefined;
  }

  if (positioned.element.type !== "text") {
    return undefined;
  }

  const xDots = offsetX(positioned, ctx, offsetDotsX);
  const yDots = offsetY(positioned, ctx, offsetDotsY);
  const widthDots = Math.max(1, mmToDots(positioned.widthMm, ctx.dpi));
  const resolvedFont = positioned.textFontOverride ?? positioned.element.font;
  const fontHeightDots = Math.max(1, mmToDots(resolvedFont.heightMm, ctx.dpi));
  const fontWidthDots = Math.max(1, mmToDots(resolvedFont.widthMm, ctx.dpi));
  const lineSpacingDots = mmToDots(resolvedFont.lineSpacingMm, ctx.dpi);
  const reversePrint = positioned.element.reversePrint;
  const align =
    resolvedFont.align === "center"
      ? "C"
      : resolvedFont.align === "right"
        ? "R"
        : "L";
  const lines = positioned.textLines ?? positioned.textValue.split("\\&");

  return {
    positioned,
    xDots,
    yDots,
    widthDots,
    fontHeightDots,
    fontWidthDots,
    fontWidthMm: resolvedFont.widthMm,
    lineSpacingDots,
    align,
    family: resolvedFont.family,
    reversePrint,
    maxLines: resolvedFont.maxLines,
    lines: lines.length > 0 ? lines : [""],
  };
}

function resolveCode128Payload(positioned: PositionedElement): {
  payload: string;
  totalModules: number;
  encodingMode: "set-b" | "subset-c";
  numericOptimizationApplied: boolean;
} {
  if (
    positioned.element.type !== "barcode" ||
    positioned.element.symbology !== "code128" ||
    positioned.textValue === undefined
  ) {
    return {
      payload: positioned.textValue ?? "",
      totalModules: estimateCode128Modules(positioned.textValue ?? "", false),
      encodingMode: "set-b" as const,
      numericOptimizationApplied: false,
    };
  }

  const canOptimize =
    positioned.element.optimizeNumeric &&
    canUseCode128SubsetC(positioned.textValue);

  if (canOptimize) {
    return {
      payload: `>;${positioned.textValue}`,
      totalModules: estimateCode128Modules(positioned.textValue, true),
      encodingMode: "subset-c",
      numericOptimizationApplied: true,
    };
  }

  return {
    payload: positioned.textValue ?? "",
    totalModules: estimateCode128Modules(positioned.textValue ?? "", false),
    encodingMode: "set-b",
    numericOptimizationApplied: false,
  };
}

function resolveBarcodeParams(
  positioned: PositionedElement,
  dpi: RenderContext["dpi"],
  totalModules: number,
): {
  moduleDots: number;
  ratio: number;
  heightDots: number;
  targetWidthDots: number;
  estimatedWidthDots: number;
  mode: BarcodeDiagnostic["mode"];
  clampReason?: BarcodeClampReason;
} {
  if (
    positioned.element.type !== "barcode" ||
    positioned.element.symbology !== "code128" ||
    positioned.textValue === undefined
  ) {
    return {
      moduleDots: 1,
      ratio: 2,
      heightDots: 1,
      targetWidthDots: 1,
      estimatedWidthDots: 1,
      mode: "fixed-module",
    };
  }

  const heightDots = Math.max(1, mmToDots(positioned.heightMm, dpi));
  const ratio = Math.max(
    2,
    Math.min(3, Math.round(positioned.element.wideToNarrowRatio)),
  );
  const targetWidthDots = Math.max(1, mmToDots(positioned.widthMm, dpi));

  if (positioned.element.widthPolicy.mode === "fixed-module") {
    const moduleDots = Math.max(
      1,
      mmToDots(positioned.element.widthPolicy.moduleWidthMm, dpi),
    );
    const estimatedWidthDots = totalModules * moduleDots;

    return {
      moduleDots,
      ratio,
      heightDots,
      targetWidthDots,
      estimatedWidthDots,
      mode: "fixed-module",
      clampReason:
        estimatedWidthDots > targetWidthDots ? "fixed-over-target" : undefined,
    };
  }

  const unclamped = Math.max(1, Math.floor(targetWidthDots / totalModules));
  const minDots = Math.max(1, positioned.element.widthPolicy.minModuleDots);
  const maxDots =
    positioned.element.widthPolicy.maxModuleDots === undefined
      ? Number.POSITIVE_INFINITY
      : Math.max(minDots, positioned.element.widthPolicy.maxModuleDots);

  let moduleDots = unclamped;
  let clampReason: BarcodeClampReason | undefined;

  if (moduleDots < minDots) {
    moduleDots = minDots;
    clampReason = "below-min";
  } else if (moduleDots > maxDots) {
    moduleDots = maxDots;
    clampReason = "above-max";
  } else if (targetWidthDots % totalModules !== 0) {
    clampReason = "fit-rounded";
  }

  const estimatedWidthDots = totalModules * moduleDots;

  return {
    moduleDots,
    ratio,
    heightDots,
    targetWidthDots,
    estimatedWidthDots,
    mode: "fit-inside",
    clampReason,
  };
}

function emitBarcodeElement(
  lines: string[],
  positioned: PositionedElement,
  ctx: RenderContext,
  encoding: ZplEncoding,
  warnings: EngineWarning[],
  barcodeDiagnostics: BarcodeDiagnostic[],
  offsetDotsX: number,
  offsetDotsY: number,
): void {
  if (
    positioned.textValue === undefined ||
    positioned.element.type !== "barcode" ||
    positioned.element.symbology !== "code128"
  ) {
    return;
  }

  const baseX = offsetX(positioned, ctx, offsetDotsX);
  const y = offsetY(positioned, ctx, offsetDotsY);
  const code128Payload = resolveCode128Payload(positioned);
  const params = resolveBarcodeParams(
    positioned,
    ctx.dpi,
    code128Payload.totalModules,
  );
  const slackDots = Math.max(
    0,
    params.targetWidthDots - params.estimatedWidthDots,
  );
  const x =
    positioned.element.hAlign === "center"
      ? baseX + Math.floor(slackDots / 2)
      : positioned.element.hAlign === "right"
        ? baseX + slackDots
        : baseX;
  const fieldData = encodeFieldData(code128Payload.payload, encoding);

  barcodeDiagnostics.push({
    elementId: positioned.element.id,
    symbology: "code128",
    mode: params.mode,
    moduleDots: params.moduleDots,
    estimatedWidthDots: params.estimatedWidthDots,
    targetWidthDots: params.targetWidthDots,
    clampReason: params.clampReason,
    code128EncodingMode: code128Payload.encodingMode,
    numericOptimizationApplied: code128Payload.numericOptimizationApplied,
  });

  if (
    params.mode === "fixed-module" &&
    params.estimatedWidthDots > params.targetWidthDots
  ) {
    warnings.push({
      code: "BARCODE_WIDTH_EXCEEDS_TARGET",
      message: `Barcode '${positioned.element.id}' estimated width ${params.estimatedWidthDots} dots exceeds target ${params.targetWidthDots} dots.`,
      path: `element:${positioned.element.id}`,
    });
  }

  lines.push(`^FO${x},${y}`);
  lines.push(`^BY${params.moduleDots},${params.ratio},${params.heightDots}`);
  lines.push(
    `^BCN,${params.heightDots},${boolFlag(positioned.element.printInterpretationLine)},${boolFlag(positioned.element.interpretationAboveCode)},${boolFlag(positioned.element.checkDigit)}`,
  );
  if (fieldData.useHex) {
    lines.push("^FH_");
  }
  lines.push(`^FD${fieldData.text}`);
  lines.push("^FS");
}

function emitQrCodeElement(
  lines: string[],
  positioned: PositionedElement,
  ctx: RenderContext,
  barcodeDiagnostics: BarcodeDiagnostic[],
  offsetDotsX: number,
  offsetDotsY: number,
): void {
  if (
    positioned.textValue === undefined ||
    positioned.element.type !== "barcode" ||
    positioned.element.symbology !== "qrcode"
  ) {
    return;
  }

  const baseX = offsetX(positioned, ctx, offsetDotsX);
  const y = offsetY(positioned, ctx, offsetDotsY);
  const mag = positioned.element.magnification;
  const ec = positioned.element.errorCorrection;
  const version = estimateQrVersion(positioned.textValue.length, ec);
  const sizeDots = qrModulesPerSide(version) * mag;
  const targetWidthDots = mmToDots(positioned.widthMm, ctx.dpi);
  const slackDots = Math.max(0, targetWidthDots - sizeDots);
  const x =
    positioned.element.hAlign === "center"
      ? baseX + Math.floor(slackDots / 2)
      : positioned.element.hAlign === "right"
        ? baseX + slackDots
        : baseX;

  lines.push(`^FO${x},${y}`);
  lines.push(`^BQN,2,${mag}`);
  lines.push(`^FD${ec}A,${positioned.textValue}`);
  lines.push("^FS");

  barcodeDiagnostics.push({
    elementId: positioned.element.id,
    symbology: "qrcode",
    magnification: mag,
    sizeDots,
  });
}

function emitShapeElement(
  lines: string[],
  positioned: PositionedElement,
  ctx: RenderContext,
  offsetDotsX: number,
  offsetDotsY: number,
): void {
  if (positioned.element.type !== "line" && positioned.element.type !== "box") {
    return;
  }

  const x = offsetX(positioned, ctx, offsetDotsX);
  const y = offsetY(positioned, ctx, offsetDotsY);
  const widthDots = Math.max(1, mmToDots(positioned.widthMm, ctx.dpi));
  const heightDots = Math.max(1, mmToDots(positioned.heightMm, ctx.dpi));
  const borderDots =
    positioned.element.type === "line"
      ? Math.max(1, mmToDots(positioned.element.thicknessMm, ctx.dpi))
      : positioned.element.fill
        ? Math.min(widthDots, heightDots)
        : Math.max(1, mmToDots(positioned.element.borderThicknessMm, ctx.dpi));
  const cornerRounding =
    positioned.element.type === "box"
      ? Math.max(0, Math.min(8, Math.round(positioned.element.cornerRounding)))
      : 0;

  lines.push(`^FO${x},${y}`);
  lines.push(`^GB${widthDots},${heightDots},${borderDots},B,${cornerRounding}`);
  lines.push("^FS");
}

function resolveCiCode(encoding: ZplEncoding): number {
  return encoding === "utf-8" ? 28 : 27;
}

function pushEngineFallbackWarning(
  warnings: EngineWarning[],
  elementId: string,
  reason: string,
): void {
  warnings.push({
    code: "TEXT_ENGINE_FALLBACK",
    message: `Text element '${elementId}' fell back to line-safe engine (${reason}).`,
    path: `element:${elementId}`,
  });
}

export function emitZpl(
  layout: LayoutResult,
  ctx: RenderContext,
  encoding: ZplEncoding,
): ZplEmitResult {
  const lines: string[] = [];
  const warnings: EngineWarning[] = [];
  const barcodeDiagnostics: BarcodeDiagnostic[] = [];
  const textDiagnostics: TextRenderDiagnostic[] = [];
  const selectedTextEngine = selectTextEngine(ctx);
  const offsetDotsX = mmToSignedDots(ctx.hardwareMarginLeftMm ?? 0, ctx.dpi);
  const offsetDotsY = mmToSignedDots(ctx.hardwareMarginTopMm ?? 0, ctx.dpi);
  const textEngineContext: TextEngineContext = {
    zpl: lines,
    warnings,
    textDiagnostics,
    renderContext: ctx,
    encoding,
    prepareFieldData: encodeFieldData,
  };

  lines.push("^XA");
  lines.push(`^CI${resolveCiCode(encoding)}`);
  lines.push(`^PW${mmToDots(layout.canvasWidthMm, ctx.dpi)}`);
  lines.push(`^LL${mmToDots(layout.canvasHeightMm, ctx.dpi)}`);

  if (ctx.printSpeed !== undefined) {
    // Zebra ^PR accepts 1–14 ips depending on model; clamp to that range.
    const printSpeed = Math.max(1, Math.min(14, Math.round(ctx.printSpeed)));
    lines.push(`^PR${printSpeed}`);
  }

  if (ctx.darkness !== undefined) {
    const darkness = Math.max(0, Math.min(30, Math.round(ctx.darkness)));
    lines.push(`~SD${String(darkness).padStart(2, "0")}`);
  }

  if (ctx.copies !== undefined) {
    const copies = Math.max(1, Math.round(ctx.copies));
    lines.push(`^PQ${copies}`);
  }

  for (const positioned of layout.elements) {
    if (positioned.element.type === "spacer") {
      continue;
    }

    if (positioned.element.type === "text") {
      const prepared = prepareTextElement(
        positioned,
        ctx,
        offsetDotsX,
        offsetDotsY,
      );
      if (prepared === undefined) {
        continue;
      }

      if (selectedTextEngine === "tb") {
        const rawValue =
          prepared.positioned.textValue ?? prepared.lines.join("\\&");
        if (prepared.align !== "L") {
          pushEngineFallbackWarning(
            warnings,
            prepared.positioned.element.id,
            "TB does not honor non-left alignment consistently",
          );
          emitWithLineSafeEngine(prepared, textEngineContext);
        } else if (
          rawValue.includes("\\&") &&
          encodeFieldData(rawValue, encoding).useHex
        ) {
          pushEngineFallbackWarning(
            warnings,
            prepared.positioned.element.id,
            "hex-escaped field data can break explicit line separators in TB mode",
          );
          emitWithLineSafeEngine(prepared, textEngineContext);
        } else {
          emitWithTbEngine(prepared, textEngineContext);
        }
      } else {
        emitWithLineSafeEngine(prepared, textEngineContext);
      }
      continue;
    }

    if (positioned.element.type === "barcode") {
      if (positioned.element.symbology === "qrcode") {
        emitQrCodeElement(
          lines,
          positioned,
          ctx,
          barcodeDiagnostics,
          offsetDotsX,
          offsetDotsY,
        );
      } else {
        emitBarcodeElement(
          lines,
          positioned,
          ctx,
          encoding,
          warnings,
          barcodeDiagnostics,
          offsetDotsX,
          offsetDotsY,
        );
      }
      continue;
    }

    emitShapeElement(lines, positioned, ctx, offsetDotsX, offsetDotsY);
  }

  lines.push("^XZ");

  return {
    zpl: `${lines.join("\n")}\n`,
    warnings,
    barcodeDiagnostics,
    textDiagnostics,
  };
}
